import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { outlets, socialLinks } from '../data/mock';
import { MapPin, Phone, Navigation, Clock } from 'lucide-react';

const Locations = () => {
  const getTimingsDisplay = (outlet) => {
    if (outlet.timings.allDays) {
      return (
        <div className="text-sm text-gray-800 font-medium">
          <p className="font-bold">Mon - Sun:</p>
          <p>{outlet.timings.allDays}</p>
        </div>
      );
    } else {
      return (
        <div className="text-sm text-gray-800 font-medium">
          <p className="font-bold">Monday:</p>
          <p className="mb-2">{outlet.timings.monday}</p>
          <p className="font-bold">Tue - Sun:</p>
          <p>{outlet.timings.tueSun}</p>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 py-12">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-black mb-4 text-white">
            FIND US <span className="text-black">NEARBY</span>
          </h1>
          <p className="text-xl text-white font-medium">Visit your nearest देशी Stuff outlet for hot, fresh parathas</p>
          <p className="text-lg text-white font-bold mt-2">Available on Zomato</p>
        </div>

        {/* Outlets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {outlets.map((outlet) => (
            <Card key={outlet.id} className="border-4 border-black hover:shadow-2xl transition-all duration-300 overflow-hidden bg-white">
              <div className="h-64 overflow-hidden">
                <iframe
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(outlet.address)}&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Map of ${outlet.name}`}
                  className="grayscale hover:grayscale-0 transition-all duration-300"
                ></iframe>
              </div>
              <CardContent className="p-6 bg-yellow-50">
                <h3 className="text-2xl font-black mb-4 text-black">{outlet.name}</h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-black mt-1 flex-shrink-0" />
                    <p className="text-gray-800 font-medium">{outlet.address}</p>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-black flex-shrink-0" />
                    <a href={`tel:${outlet.phone}`} className="text-gray-800 font-bold hover:text-black transition-colors">
                      {outlet.phone}
                    </a>
                  </div>

                  <div className="flex items-start space-x-3 bg-yellow-100 p-3 rounded-lg border-2 border-yellow-600">
                    <Clock className="w-5 h-5 text-black mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      {getTimingsDisplay(outlet)}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <a href={`tel:${outlet.phone}`} className="flex-1">
                    <Button className="w-full font-bold bg-black hover:bg-gray-900 text-yellow-400">
                      <Phone className="w-4 h-4 mr-2" />
                      Call Now
                    </Button>
                  </a>
                  <a 
                    href={outlet.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button variant="outline" className="w-full font-bold border-2 border-black text-black hover:bg-black hover:text-yellow-400">
                      <Navigation className="w-4 h-4 mr-2" />
                      Directions
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* General Info */}
        <Card className="border-4 border-black bg-white shadow-xl mb-8">
          <CardContent className="p-8 text-center">
            <h2 className="text-3xl font-black mb-4 text-black">OPENING HOURS</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left max-w-3xl mx-auto">
              <div className="bg-yellow-100 p-4 rounded-lg border-2 border-yellow-600">
                <h3 className="font-black text-lg mb-2 text-black">Most Outlets:</h3>
                <p className="text-gray-800 font-medium">Lunch: 12:00 PM - 3:00 PM</p>
                <p className="text-gray-800 font-medium">Dinner: 6:00 PM - 12:00 AM</p>
              </div>
              <div className="bg-yellow-100 p-4 rounded-lg border-2 border-yellow-600">
                <h3 className="font-black text-lg mb-2 text-black">Motera:</h3>
                <p className="text-gray-800 font-medium">Dinner Only: 6:00 PM - 12:00 AM</p>
              </div>
            </div>
            <p className="text-sm text-gray-700 font-medium mt-4">Check individual outlet timings above for details</p>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <div className="bg-black rounded-2xl p-12 text-center text-yellow-400 shadow-2xl border-4 border-yellow-600">
          <h3 className="text-4xl font-black mb-4">CAN'T VISIT? ORDER ONLINE!</h3>
          <p className="text-xl mb-8 font-bold">Get hot parathas delivered via Zomato or WhatsApp</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://wa.me/919601382424" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="text-lg font-bold bg-yellow-500 hover:bg-yellow-400 text-black px-8 py-6">
                Order on WhatsApp
              </Button>
            </a>
            <a href={socialLinks.zomato} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" className="text-lg font-bold border-2 border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-black px-8 py-6">
                Order on Zomato
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Locations;
